import React, { useContext, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { useInfiniteQuery } from "react-query";
import { toast } from "react-toastify";
import { apiAdmin, getApiErrorMessage } from "../../services/api";
import { resolveMentorImage, DEFAULT_MENTOR_AVATAR as placeholderAvatar } from "../../utils/mentorshipUtils";
import MentorshipCard from "./MentorshipCard";
import { getCategoryNameById } from "./data/mentorshipData";
import { Language } from "../../App";
import { preloadImages } from "../../utils/imageCache";

// Everything for the mentor list — the API call, the shimmer, and the grid
// — lives in this one file so it's obvious where the data comes from.

// Bigger screens have more room, so fetch bigger pages instead of paging
// through many small ones.
function getResponsivePerPage() {
  const width = window.innerWidth;
  if (width >= 2600) return 24;
  if (width >= 2000) return 20;
  if (width >= 1600) return 16;
  if (width >= 992) return 12;
  if (width >= 576) return 8;
  return 6;
}

// Columns scale up to 4 on >1600px, 5 on >2000px, 6 on >2600px
function getResponsiveColumns() {
  const width = window.innerWidth;
  if (width >= 2600) return 6;
  if (width >= 2000) return 5;
  if (width >= 1600) return 4;
  if (width >= 992) return 3;
  if (width >= 576) return 2;
  return 1;
}

// In-memory module cache for mentor home page responses (resets on page refresh)
const mentorsMemoryCache = {};

// Clean up any old persistent mentors cache from sessionStorage/localStorage
try {
  Object.keys(sessionStorage)
    .filter((k) => k.startsWith("mr_mentors_cache_"))
    .forEach((k) => sessionStorage.removeItem(k));
  Object.keys(localStorage)
    .filter((k) => k.startsWith("mr_mentors_cache_"))
    .forEach((k) => localStorage.removeItem(k));
} catch (e) {}

const getMentorsCacheKey = (categoryName) => `mr_mentors_cache_${categoryName || "All"}`;

export function getCachedMentorsPage(categoryName) {
  const key = getMentorsCacheKey(categoryName);
  const data = mentorsMemoryCache[key];
  if (data?.data?.mentors?.data) {
    try {
      const urls = data.data.mentors.data.map((item) => resolveMentorImage(item)).filter(Boolean);
      preloadImages(urls, true);
    } catch (e) {}
    return data;
  }
  return null;
}

export function setCachedMentorsPage(categoryName, data) {
  if (!data?.status || !data?.data?.mentors?.data) return;
  const key = getMentorsCacheKey(categoryName);
  mentorsMemoryCache[key] = data;
  try {
    const urls = data.data.mentors.data.map((item) => resolveMentorImage(item)).filter(Boolean);
    preloadImages(urls, true);
  } catch (e) {}
}

// apiAdmin already attaches the Bearer token automatically (see
// src/services/api.js interceptor) — category_id is always sent, defaulting
// to the literal "All" when no specific category is selected.
async function fetchMentors({ page, per_page, category_id, lang }) {
  const res = await apiAdmin.post("user/mentor/home", {
    page,
    per_page,
    category_id: category_id || "All",
  });
  // The same response also carries doctor-level stats and ad slots
  // (mentor_center/mentor_left) alongside the mentors page — stash the
  // whole thing in localStorage so BecomeMentorCard/MentorshipAdCard/
  // MentorshipBanner can show it without hitting this endpoint themselves.
  if (res.data?.status) {
    localStorage.setItem("mr_home_data", JSON.stringify(res.data.data || {}));
    // Same-tab localStorage writes don't fire the native "storage" event —
    // MentorshipBanner/MentorshipAdCard listen for this to know the moment
    // real data is ready, instead of just showing an empty state forever.
    window.dispatchEvent(new Event("mrHomeDataUpdated"));
    if (page === 1) {
      setCachedMentorsPage(category_id, res.data);
    }
  } else {
    toast.error(getApiErrorMessage(res.data, lang?.mentorship_load_mentors_error || "Couldn't load mentors. Please try again."));
  }
  return res.data;
}

function mapMentor(raw) {
  const profile = raw.mentor_profile || {};
  return {
    ...raw,
    image: resolveMentorImage(raw),
    specialty: profile.specialty || raw.category || raw.subcategory || "",
    bio: profile.bio || raw.bio || "",
  };
}

const GRID_GAP = "20px";

// Mirrors MentorshipCard.jsx's exact wrapper (border, radius, background,
// padding, gap) and section structure — just with shimmer blocks standing
// in for the photo/name/pill/bio/buttons.
function CardShimmer() {
  return (
    <div
      className="mentorship-card-cap mentorship-card-cap--shimmer"
      style={{
        width: "100%",
        borderRadius: "26px",
        border: "0.5px solid #D7E0EA",
        background: "linear-gradient(180deg, #DAFAFF 0px, #EFFDFF 180px, #FFFFFF 100px, #FFFFFF 100%)",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        boxSizing: "border-box",
        pointerEvents: "none",
        cursor: "default",
      }}
    >
      {/* Doctor photo */}
      <div
        className="mentorship-shimmer-block"
        style={{ width: "100%", height: "210px", borderRadius: "24px", border: "2px solid #FFFFFF" }}
      />

      {/* Name / specialty / bio */}
      <div style={{ textAlign: "center" }}>
        <div
          className="mentorship-shimmer-block mx-auto"
          style={{ width: "70%", height: "18px", borderRadius: "4px", margin: "0 0 8px" }}
        />
        <div
          className="mentorship-shimmer-block mx-auto"
          style={{ width: "50%", height: "22px", borderRadius: "6px", margin: "0 0 10px" }}
        />

        <hr style={{ border: "none", borderTop: "1px solid #EAEEF2", margin: "0 0 10px" }} />

        <div style={{ borderTop: "1px solid #E6E7EA", paddingTop: "8px" }}>
          <div className="mentorship-shimmer-block mx-auto" style={{ width: "100%", height: "12px", borderRadius: "4px", margin: "0 0 6px" }} />
          <div className="mentorship-shimmer-block mx-auto" style={{ width: "100%", height: "12px", borderRadius: "4px", margin: "0 0 6px" }} />
          <div className="mentorship-shimmer-block mx-auto" style={{ width: "70%", height: "12px", borderRadius: "4px" }} />
        </div>
      </div>

      {/* Buttons */}
      <div>
        <div
          className="mentorship-shimmer-block"
          style={{ width: "100%", height: "36px", borderRadius: "999px", marginBottom: "10px" }}
        />
        <div className="mentorship-shimmer-block" style={{ width: "100%", height: "40px", borderRadius: "999px" }} />
      </div>
    </div>
  );
}

function MentorshipGrid({ activeCategoryId, onViewProfile, onApply }) {
  const { lang } = useContext(Language);
  const [perPage, setPerPage] = useState(getResponsivePerPage);
  const [columns, setColumns] = useState(getResponsiveColumns);

  useEffect(() => {
    const handleResize = () => {
      setPerPage(getResponsivePerPage());
      setColumns(getResponsiveColumns());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // "All" (id 0 / unset) resolves to null, meaning no category_id is sent.
  const categoryName = getCategoryNameById(activeCategoryId);

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["mentorshipMentors", categoryName, perPage],
    queryFn: ({ pageParam = 1 }) =>
      fetchMentors({ page: pageParam, per_page: perPage, category_id: categoryName, lang }),
    initialData: () => {
      const cached = getCachedMentorsPage(categoryName);
      if (cached) {
        return {
          pages: [cached],
          pageParams: [1],
        };
      }
      return undefined;
    },
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.mentors;
      const nextPage = (pagination?.current_page || 0) + 1;
      return nextPage <= (pagination?.last_page || 0) ? nextPage : undefined;
    },
    refetchOnWindowFocus: false,
    onError: (err) =>
      toast.error(getApiErrorMessage(err?.response?.data, lang?.mentorship_load_mentors_error || "Couldn't load mentors. Please try again.")),
  });

  const mentors = useMemo(() => {
    const rawList = data?.pages?.flatMap((page) => page.data?.mentors?.data || []) || [];
    const seen = new Set();
    const list = [];
    for (const item of rawList) {
      if (item && item.id && !seen.has(item.id)) {
        seen.add(item.id);
        list.push(mapMentor(item));
      } else if (item && !item.id) {
        list.push(mapMentor(item));
      }
    }
    return list;
  }, [data]);

  // Preload and decode all mentor images in memory so cards show them instantly
  useEffect(() => {
    if (mentors.length > 0) {
      const urls = mentors.map((m) => m.image).filter(Boolean);
      preloadImages(urls, true);
    }
  }, [mentors]);

  const showShimmer = (isLoading || (isFetching && !isFetchingNextPage)) && !data;
  const shimmerCount = Math.max(columns * 3, 12);

  return (
    <>
      <style>{`
        .mentorship-shimmer-block {
          background: linear-gradient(90deg, #F1F5F9 0%, #E2E8F0 50%, #F1F5F9 100%);
          background-size: 200% 100%;
          animation: mrShimmerWave 1.6s ease-in-out infinite;
        }
        @keyframes mrShimmerWave {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .mentorship-card-cap--shimmer,
        .mentorship-card-cap--shimmer:hover {
          transform: none !important;
          box-shadow: none !important;
          border-color: #D7E0EA !important;
          pointer-events: none !important;
          cursor: default !important;
        }
        @media (max-width: 575.98px) {
          .mentorship-card-cap {
            max-width: none !important;
          }
        }
      `}</style>

      {showShimmer ? (
        <div className="mentorship-grid my-3">
          {Array.from({ length: shimmerCount }).map((_, n) => <CardShimmer key={n} />)}
        </div>
      ) : !mentors.length ? (
        <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 px-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{ width: 72, height: 72, backgroundColor: "#E8FBFF" }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 20v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2M10 10a4 4 0 100-8 4 4 0 000 8zM23 20v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
                stroke="#1DC1DD"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="19" cy="7" r="1.2" fill="#1DC1DD" />
            </svg>
          </div>

          <p className="roboto-medium fs-16 mb-1" style={{ color: "#1F1F1F" }}>
            {lang?.mentorship_no_mentors_title || "No mentors found"}
          </p>

          <p className="text-gray fs-14 roboto-regular mb-0" style={{ maxWidth: 280 }}>
            {lang?.mentorship_no_mentors_found ||
              "We couldn't find any mentors for this specialty. Try a different category or check back later."}
          </p>
        </div>
      ) : (
        <div className="mentorship-grid-container w-100 position-relative">
          <InfiniteScroll
            pageStart={0}
            loadMore={() => {
              if (!isFetchingNextPage && hasNextPage) {
                fetchNextPage();
              }
            }}
            hasMore={Boolean(hasNextPage)}
            useWindow={true}
            threshold={350}
            loader={
              <div className="mentorship-grid my-3" key="mentorship-grid-loader">
                {Array.from({ length: columns }).map((_, i) => (
                  <CardShimmer key={`loader-shimmer-${i}`} />
                ))}
              </div>
            }
          >
            <div className="mentorship-grid my-3">
              {mentors.map((mentor, index) => (
                <MentorshipCard
                  key={`${mentor.id}-${index}`}
                  mentor={mentor}
                  onViewProfile={onViewProfile}
                  onApply={onApply}
                />
              ))}
            </div>
          </InfiniteScroll>
        </div>
      )}
    </>
  );
}

export default React.memo(MentorshipGrid);
